import { Button, Grid } from "@mui/material";
import { push } from "connected-react-router";
import { FC, useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import usePermitGuard from "@/authProvider/usePermitGuard";
import { Content } from "@/components/content/Content";
import DocumentTypeEditor from "@/components/securefiles/DocumentTypeEditor";
import { MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES } from "@/constants";
import { useLmStatesPropagate } from "@/contexts/LocalMessageHookProtocols/useLmStatesPropagate";
import { LocalMessageLocation, useLocalMessagingRoot } from "@/contexts/LocalMessagingRoot";
import CreateOrEdit from "@/controller/CreateOrEdit";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import WithSave from "@/controller/WithSave";
import { DocumentTemplateInput, DocumentTemplateResponseDto } from "@/gen/api/securefile";
import { OdResourcePageProps } from "@/types/resource";
import { useResourceRegister } from "@/utils/resourceRegister";


const CreateDocumentType: FC<OdResourcePageProps> = ({
  options, resource,
}) => {
  useResourceRegister({name: 'secure-files-doc-tmpl-upload'})
  const dispatch = useDispatch()
  const [states, setStates] = useState({
    dirty: false,
  })

  usePermitGuard([[MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES]])
  
  const { LocalMessagingRootHost, subscribe, publish } = useLocalMessagingRoot()
  const { assembleSubStates } = useLmStatesPropagate({ 
    key: 'root',
    asUpperStage: {
      dirtyStateInformed: {
        handler() {
          return (_, dirty) => setStates(v => {
            return v.dirty !== dirty ? {...v, dirty} : v
          })
        },
        deps: []
      }
    }
  }, {subscribe, publish })

  const handleSave = (save: (record:any,callbacks?:any)=>void) => async () => {
    try {
      const result = await assembleSubStates(['editor'])
      const payload = result.editor!
  
      save(payload, {
        onSuccess: ({data}:{data:DocumentTemplateResponseDto}) => {
          dispatch(push(`/${resource}/${data.id}/show`))
        }
      })
    } catch (error) {
      return
    }
  }
  
  const handlers = useDefaultSavedHandlers("Create Document Type")
  
  return (
    <Content 
      pageName="Create Document Type" 
      resourceName="Create" 
      breadcrumbs={[...(options?.breadcrumbs ?? []), 
        { label: 'Document Types', path: `/${resource}` }
      ]}
      mainActions={
        <>
          <CreateOrEdit resource={resource} basePath="" {...handlers}>
            <WithSave 
              render={({save, saving}) =>
                <Button
                  color="primary"
                  disabled={saving || !states.dirty}
                  variant="contained"
                  onClick={handleSave(save)}
                >
                  Save
                </Button>}
            />
          </CreateOrEdit>
          <Button
            color="primary"
            variant="outlined"
            component={RouterLink}
            to={`/${resource}`}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <LocalMessagingRootHost>
            <LocalMessageLocation id="editor">
              <DocumentTypeEditor title="New Document Type" />
            </LocalMessageLocation>
          </LocalMessagingRootHost>
        </Grid>
      </Grid>
    </Content>
  )
}

export default CreateDocumentType;
